import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';

import { usePortalContext } from '@/contexts/PortalProvider';
import { AppDispatch } from '@/data/store';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useIsSuperReaderUser } from '@/hooks/permissions/useIsSuperReaderUser';
import {
  useFeatureCreateReservationF1,
  useFeatureCreateReservationClientPortal,
} from '@/hooks/features/useFeatureCreateReservation';

import { fetchTransportationAttributesAsync } from '@/data/accounting.slice';
import {
  fetchPassengerClientDataAsync,
  selectPassengerClientData,
  setLoadedPassengerClientData,
} from '@/data/client.slice';
import {
  selectLoadingCurrentPassenger,
  selectPassengerEventEmitted,
  selectCurrentPassenger,
  fetchPassengerAsync,
  setCurrentPassenger,
  setPassengerEventEmitted,
} from '@/data/passenger.slice';
import {
  fetchPassengerBenefitSummaryAsync,
  selectCanceledReservation,
  selectCanceledTripLeg,
  selectReservationEventEmitted,
  selectSavedReservation,
  selectSavedTripLeg,
  setReservationEventEmitted,
} from '@/data/reservation.slice';
import PassengerHeader from './PassengerHeader';
import PassengerDetails from './PassengerDetails';
import ReservationDialog from '@/components/home/reservations/ReservationDialog';
import {
  RESERVATION_REFETCH_DEBOUNCE_MS,
  RESERVATION_REFETCH_MAX_WAIT_MS,
} from '@/data/constants';

const PREFIX = 'Passenger';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default function Passenger() {
  const dispatch: AppDispatch = useAppDispatch();
  const t = useCommonTranslation();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isClientPortal } = usePortalContext();
  const passenger = useAppSelector(selectCurrentPassenger);
  const savedReservation = useAppSelector(selectSavedReservation);
  const savedTripLeg = useAppSelector(selectSavedTripLeg);
  const canceledReservation = useAppSelector(selectCanceledReservation);
  const canceledTripLeg = useAppSelector(selectCanceledTripLeg);
  const loadingPassenger = useAppSelector(selectLoadingCurrentPassenger);
  const loadedCurrentPassenger = params.id === passenger?.passengerUUID;

  const passengerClientData = useAppSelector(selectPassengerClientData);
  const isSuperReaderUser = useIsSuperReaderUser();
  const passengerEventDetail = useAppSelector(selectPassengerEventEmitted);
  const reservationEventDetail = useAppSelector(selectReservationEventEmitted);

  const createReservationRefactor =
    (!isClientPortal && useFeatureCreateReservationF1()) ||
    (isClientPortal && useFeatureCreateReservationClientPortal());

  const [tripDialogOpen, setTripDialogOpen] = useState(false);

  const fetchPassengerBenefitSummary = useCallback(() => {
    if (params.id && passenger?.passengerUUID) {
      dispatch(
        fetchPassengerBenefitSummaryAsync(
          passenger.passengerUUID,
          passenger.servicePlans.map(({ servicePlanUUID }) => servicePlanUUID),
          DateTime.now().toISODate(),
        ),
      );
    }
  }, [params.id, passenger, dispatch]);

  // A full STO cancel emits a ReservationCanceled event per reservation, each
  // of which previously triggered an immediate benefit-summary fetch. Debounce
  // it the same way the reservation list is debounced in PassengerReservations:
  // settle 1s after events stop, refresh at most every 5s during the cascade.
  const debouncedFetchPassengerBenefitSummary = useMemo(
    () =>
      debounce(
        () => {
          fetchPassengerBenefitSummary();
        },
        RESERVATION_REFETCH_DEBOUNCE_MS,
        { maxWait: RESERVATION_REFETCH_MAX_WAIT_MS },
      ),
    [fetchPassengerBenefitSummary],
  );

  useEffect(() => {
    return () => {
      debouncedFetchPassengerBenefitSummary.cancel();
    };
  }, [debouncedFetchPassengerBenefitSummary]);

  const handleCreateTrip = () => {
    if (createReservationRefactor) {
      navigate(`/passengers/${params.id}/create-reservation`);
    } else {
      setTripDialogOpen(true);
    }
  };

  useEffect(() => {
    dispatch(fetchTransportationAttributesAsync());
  }, []);

  useEffect(() => {
    if (params.id) {
      dispatch(setLoadedPassengerClientData(false));
      dispatch(fetchPassengerAsync(params.id));
    }
  }, [params.id, dispatch]);

  useEffect(() => {
    if (loadedCurrentPassenger && passenger) {
      dispatch(
        fetchPassengerClientDataAsync(
          passenger.clientBC,
          passenger.servicePlans,
          passenger.passengerUUID,
        ),
      );
    }
  }, [loadedCurrentPassenger, passenger]);

  useEffect(() => {
    const reservationChanged =
      savedReservation ||
      savedTripLeg ||
      canceledReservation ||
      canceledTripLeg;
    if (passenger?.passengerUUID && reservationChanged) {
      fetchPassengerBenefitSummary();
    }
  }, [
    passenger,
    savedReservation,
    savedTripLeg,
    canceledReservation,
    canceledTripLeg,
    fetchPassengerBenefitSummary,
  ]);

  useEffect(() => {
    if (
      passenger?.passengerUUID &&
      passengerEventDetail?.passengerUUID === passenger?.passengerUUID
    ) {
      dispatch(setCurrentPassenger(passengerEventDetail));
      dispatch(setPassengerEventEmitted(null));
    }
  }, [passengerEventDetail]);

  useEffect(() => {
    if (
      passenger?.passengerUUID &&
      reservationEventDetail?.detail.passengerUUID === passenger?.passengerUUID
    ) {
      debouncedFetchPassengerBenefitSummary();
      dispatch(setReservationEventEmitted(null));
    }
  }, [reservationEventDetail, debouncedFetchPassengerBenefitSummary]);

  return (
    <StyledBox className={classes.container}>
      <PassengerHeader
        name={`${[passenger?.firstName, passenger?.middleName, passenger?.lastName].filter(Boolean).join(' ') || ''}`}
        createDisabled={!passenger}
        createHidden={isSuperReaderUser}
        onCreateTrip={handleCreateTrip}
      />
      <PassengerDetails
        passengerUUID={params.id!}
        passenger={passenger}
        loadingPassenger={loadingPassenger}
        servicePlans={passengerClientData?.servicePlans || []}
      />
      {passenger && tripDialogOpen && (
        <ReservationDialog
          title={t('reservation.createTrip')}
          open={tripDialogOpen}
          passenger={passenger!}
          servicePlans={passengerClientData?.servicePlans || []}
          close={() => setTripDialogOpen(false)}
          isPortal={false}
        />
      )}
    </StyledBox>
  );
}
